/**
 * Filter Parameter Mapping Utilities
 *
 * Provides centralized mapping between internal filter state and API parameters
 * to eliminate duplicate code and ensure consistency across components.
 */

/**
 * Maps internal filter state property names to API parameter names
 */
export const FILTER_PARAM_MAP = {
  // Internal state -> API parameter
  mineralType: 'mineralType',
  specificCommodities: 'commodities',
  countries: 'countries',
  mineTypes: 'mineTypes',
  mineStatus: 'status',
  includeArtisanal: 'includeArtisanal'
};

/**
 * Maps API parameter names back to internal filter state property names
 */
export const REVERSE_FILTER_PARAM_MAP = {
  // API parameter -> Internal state
  mineralType: 'mineralType',
  commodities: 'specificCommodities',
  countries: 'countries',
  mineTypes: 'mineTypes',
  status: 'mineStatus',
  includeArtisanal: 'includeArtisanal'
};

/**
 * Transform internal filter state to API parameters
 * @param {Object} filters - Internal filter state object
 * @returns {Object} - API-compatible filter parameters
 */
export function mapFilterParams(filters) {
  const mapped = {};

  Object.keys(FILTER_PARAM_MAP).forEach(internalKey => {
    const apiKey = FILTER_PARAM_MAP[internalKey];
    if (filters[internalKey] !== undefined) {
      mapped[apiKey] = filters[internalKey];
    }
  });

  return mapped;
}

/**
 * Transform API parameters back to internal filter state format
 * @param {Object} apiParams - API parameter object
 * @returns {Object} - Internal filter state compatible object
 */
export function reverseMapFilterParams(apiParams) {
  const mapped = {};

  Object.keys(REVERSE_FILTER_PARAM_MAP).forEach(apiKey => {
    const internalKey = REVERSE_FILTER_PARAM_MAP[apiKey];
    if (apiParams[apiKey] !== undefined) {
      mapped[internalKey] = apiParams[apiKey];
    }
  });

  return mapped;
}

/**
 * Get all supported filter parameter names for API calls
 * @returns {Array<string>} - Array of API parameter names
 */
export function getAPIFilterParams() {
  return Object.values(FILTER_PARAM_MAP);
}

/**
 * Get all supported internal filter state property names
 * @returns {Array<string>} - Array of internal property names
 */
export function getInternalFilterParams() {
  return Object.keys(FILTER_PARAM_MAP);
}

/**
 * Validate that a filter object contains only known parameters
 * @param {Object} filters - Filter object to validate
 * @param {boolean} isAPIFormat - Whether to validate against API or internal format
 * @returns {Object} - { isValid: boolean, unknownParams: Array<string> }
 */
export function validateFilterParams(filters, isAPIFormat = false) {
  const validParams = isAPIFormat ? getAPIFilterParams() : getInternalFilterParams();
  const unknownParams = Object.keys(filters).filter(key => !validParams.includes(key));

  return {
    isValid: unknownParams.length === 0,
    unknownParams
  };
}